import React, { useEffect, useRef, useState } from "react";
import { useReactToPrint } from "react-to-print";
import { AnimatePresence, motion } from "framer-motion";

// API & Constants
import { ERROR_MESSAGE_SUPPORT } from "../../common/helpersTxt";
import CashierService from "../../api/cashier";

// Components
// MUI Components
import {
  alpha,
  Box,
  Button,
  CircularProgress,
  IconButton,
  Paper,
  Skeleton,
  Stack,
  Tooltip,
  Typography,
  useTheme,
} from "@mui/material";

// Icons
import LocalPrintshopIcon from "@mui/icons-material/LocalPrintshop";
import ReceiptLongIcon from "@mui/icons-material/ReceiptLong";
import RefreshIcon from "@mui/icons-material/Refresh";

// Utils
import { dispatchSnackBarMessage } from "../../utils/snackbar";
import { dateTimeMask } from "../../common/dateMask";
import CashierClosingTicket from "./ticket/CashierClosingTicket";

// Animation variants
const containerVariants = {
  hidden: { opacity: 0 },
  visible: {
    opacity: 1,
    transition: {
      duration: 0.5,
      when: "beforeChildren",
      staggerChildren: 0.1,
    },
  },
  exit: {
    opacity: 0,
    transition: { duration: 0.3 },
  },
};

const itemVariants = {
  hidden: { y: 20, opacity: 0 },
  visible: {
    y: 0,
    opacity: 1,
    transition: { type: "spring", stiffness: 300, damping: 24 },
  },
  exit: {
    y: -20,
    opacity: 0,
    transition: { duration: 0.2 },
  },
};

const buttonVariants = {
  hover: {
    scale: 1.05,
    boxShadow: "0 5px 15px rgba(0,0,0,0.1)",
  },
  tap: {
    scale: 0.95,
  },
};

export default function ShowCashier({ show, cashier, hideHeader = false, onActionsReady }) {
  const theme = useTheme();
  const printComponentRef = useRef(null);

  const [loadingCashier, setLoadingCashier] = useState(true);
  const [reportData, setReportData] = useState(null);
  const [printingStatus, setPrintingStatus] = useState(null);

  useEffect(() => {
    if (show && cashier) {
      loadReportInfos();
    }
  }, [show, cashier]);

  const showErrorMessage = () => {
    dispatchSnackBarMessage(
      `cashier-info-${new Date()}`,
      ERROR_MESSAGE_SUPPORT,
      "error"
    );
  };

  const loadReportInfos = async () => {
    if (!cashier || !cashier.id) {
      console.error("Cashier or cashier ID is missing");
      setLoadingCashier(false);
      return;
    }

    setLoadingCashier(true);

    try {
      const { data } = await CashierService.getCashierStoredReport({
        cashier_id: cashier.id,
      });

      setReportData(data);
    } catch (e) {
      console.error(e);
      showErrorMessage();
    } finally {
      setLoadingCashier(false);
    }
  };

  // Fixed handlePrint function - using contentRef property like in the working example
  const handlePrint = useReactToPrint({
    // Use contentRef directly instead of content function
    contentRef: printComponentRef,

    onBeforeGetContent: () => {
      // Check if we have content before printing
      if (!printComponentRef.current || !isDataLoaded) {
        dispatchSnackBarMessage(
          `show-cashier-report-${Date.now()}`,
          "Não há dados para imprimir",
          "error"
        );
        return Promise.reject("No printable content");
      }

      setPrintingStatus("preparing");
      return Promise.resolve();
    },

    onBeforePrint: () => {
      setPrintingStatus("printing");
      dispatchSnackBarMessage(
        `show-cashier-report-${Date.now()}`,
        "Enviando para impressão",
        "info"
      );
      return Promise.resolve();
    },

    onAfterPrint: () => {
      setPrintingStatus("success");
      dispatchSnackBarMessage(
        `show-cashier-report-${Date.now()}`,
        "Documento enviado para impressão com sucesso!",
        "success"
      );
      setTimeout(() => setPrintingStatus(null), 2000);
    },

    onPrintError: (errorLocation, error) => {
      console.error(`Print error at ${errorLocation}:`, error);
      setPrintingStatus("error");
      dispatchSnackBarMessage(
        `show-cashier-report-${Date.now()}`,
        "Erro ao imprimir, tente novamente",
        "error"
      );
      setTimeout(() => setPrintingStatus(null), 2000);
    },

    // Additional options to help with print issues
    removeAfterPrint: false,
    suppressErrors: false,
    copyStyles: true, // Important for making sure styles are copied to the print window
  });

  // Loading skeletons
  const renderSkeletons = () => (
    <Box sx={{ p: 3 }}>
      <Skeleton
        variant="rectangular"
        width="100%"
        height={40}
        sx={{ mb: 2, borderRadius: 1 }}
      />
      <Skeleton
        variant="rectangular"
        width="60%"
        height={30}
        sx={{ mb: 2, borderRadius: 1 }}
      />
      <Skeleton
        variant="rectangular"
        width="100%"
        height={200}
        sx={{ mb: 2, borderRadius: 1 }}
      />
      <Stack direction="row" spacing={2} sx={{ mb: 2 }}>
        <Skeleton
          variant="rectangular"
          width="50%"
          height={40}
          sx={{ borderRadius: 1 }}
        />
        <Skeleton
          variant="rectangular"
          width="50%"
          height={40}
          sx={{ borderRadius: 1 }}
        />
      </Stack>
      <Skeleton
        variant="rectangular"
        width="100%"
        height={150}
        sx={{ borderRadius: 1 }}
      />
    </Box>
  );

  // Make sure we have all required data before enabling printing
  const isDataLoaded =
    !loadingCashier &&
    cashier &&
    reportData &&
    cashier.date_time_begin &&
    (cashier.started_finish_at || cashier.date_time_end);

  // Report actions to parent when hideHeader is used
  useEffect(() => {
    if (hideHeader && onActionsReady) {
      onActionsReady({
        handlePrint,
        loadReportInfos,
        isDataLoaded: !!isDataLoaded,
        printingStatus,
      });
    }
  }, [hideHeader, onActionsReady, isDataLoaded, printingStatus]);

  // Get the correct end date (fallback to date_time_end if started_finish_at is not available)
  const endDate = cashier?.started_finish_at || cashier?.date_time_end;

  return (
    <AnimatePresence mode="wait">
      {loadingCashier ? (
        <motion.div
          key="loading"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: 0.3 }}
        >
          <Paper
            elevation={0}
            sx={{
              p: 3,
              borderRadius: 2,
              border: `1px solid ${alpha(theme.palette.divider, 0.2)}`,
              bgcolor: alpha(theme.palette.background.paper, 0.8),
            }}
          >
            <Box
              sx={{
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                justifyContent: "center",
                py: 3,
              }}
            >
              <CircularProgress size={60} thickness={4} />
              <Typography variant="h6" color="text.secondary" sx={{ mt: 3 }}>
                Carregando dados do caixa...
              </Typography>
            </Box>
            {renderSkeletons()}
          </Paper>
        </motion.div>
      ) : (
        <motion.div
          key="content"
          variants={containerVariants}
          initial="hidden"
          animate="visible"
          exit="exit"
        >
          <Paper
            elevation={0}
            sx={{
              borderRadius: 2,
              border: `1px solid ${alpha(theme.palette.divider, 0.2)}`,
              bgcolor: alpha(theme.palette.background.paper, 0.8),
              overflow: "hidden",
            }}
          >
            {/* Header with title and action buttons */}
            {!hideHeader && (
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "space-between",
                  p: 2,
                  bgcolor: alpha(theme.palette.primary.light, 0.05),
                  borderBottom: `1px solid ${alpha(theme.palette.divider, 0.2)}`,
                }}
              >
                <Stack direction="row" alignItems="center" spacing={1.5}>
                  <Box
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      bgcolor: alpha(theme.palette.primary.main, 0.1),
                      color: theme.palette.primary.main,
                      borderRadius: 1.5,
                      p: 0.75,
                      width: 36,
                      height: 36,
                    }}
                  >
                    <ReceiptLongIcon fontSize="small" />
                  </Box>
                  <Box>
                    <Typography variant="h6" fontWeight={600}>
                      Relatório de Caixa
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      {cashier &&
                        `ID: ${cashier.id} • ${dateTimeMask(
                          cashier.date_time_begin
                        )}`}
                    </Typography>
                  </Box>
                </Stack>

                <Stack direction="row" spacing={1}>
                  <motion.div
                    whileHover="hover"
                    whileTap="tap"
                    variants={buttonVariants}
                  >
                    <Tooltip title="Atualizar dados">
                      <IconButton
                        onClick={loadReportInfos}
                        color="primary"
                        sx={{
                          bgcolor: alpha(theme.palette.primary.main, 0.1),
                          "&:hover": {
                            bgcolor: alpha(theme.palette.primary.main, 0.2),
                          },
                        }}
                      >
                        <RefreshIcon />
                      </IconButton>
                    </Tooltip>
                  </motion.div>

                  <motion.div
                    whileHover="hover"
                    whileTap="tap"
                    variants={buttonVariants}
                  >
                    <Button
                      variant="contained"
                      color="primary"
                      startIcon={<LocalPrintshopIcon />}
                      onClick={handlePrint}
                      disabled={
                        !isDataLoaded ||
                        printingStatus === "printing" ||
                        printingStatus === "preparing"
                      }
                      sx={{
                        position: "relative",
                        overflow: "hidden",
                      }}
                    >
                      {printingStatus === "printing" ||
                      printingStatus === "preparing" ? (
                        <>
                          <CircularProgress
                            size={24}
                            sx={{
                              color: "white",
                              position: "absolute",
                              left: "50%",
                              marginLeft: "-12px",
                            }}
                          />
                          <span style={{ opacity: 0 }}>Imprimir</span>
                        </>
                      ) : (
                        "Imprimir"
                      )}
                    </Button>
                  </motion.div>
                </Stack>
              </Box>
            )}

            {/* Ticket content - This is the printable area */}
            <Box sx={{ p: 3 }}>
              <motion.div variants={itemVariants}>
                {/* The ref is attached directly to the Box element containing the printable content */}
                <Box
                  ref={printComponentRef}
                  sx={{
                    p: 3,
                    border: `1px solid ${alpha(theme.palette.divider, 0.3)}`,
                    borderRadius: 1,
                    bgcolor: theme.palette.background.paper,
                    boxShadow: "0 1px 3px rgba(0,0,0,0.05)",
                    maxWidth: "100%",
                    overflow: "auto",
                    minHeight: isDataLoaded ? "auto" : "200px",
                    display: "block",
                  }}
                >
                  {isDataLoaded ? (
                    <CashierClosingTicket
                      startDate={cashier.date_time_begin}
                      endDate={endDate}
                      cashier={cashier}
                      reportData={reportData}
                    />
                  ) : (
                    <Typography
                      variant="body1"
                      textAlign="center"
                      color="text.secondary"
                    >
                      Sem dados disponíveis para exibição
                    </Typography>
                  )}
                </Box>
              </motion.div>

              <motion.div variants={itemVariants}>
                <Typography
                  variant="caption"
                  color="text.secondary"
                  sx={{ display: "block", textAlign: "center", mt: 2 }}
                >
                  Utilize o botão "Imprimir" para gerar uma cópia física ou PDF
                  deste relatório
                </Typography>
              </motion.div>
            </Box>
          </Paper>
        </motion.div>
      )}
    </AnimatePresence>
  );
}
